import { toast } from "react-hot-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Trash2, AlertTriangle, X } from "lucide-react";
import { useDeleteFeedback } from "@/hooks/api-hooks";

interface Feedback {
  id: string;
  responseText: string;
  responseRating?: number;
  isAnonymous: boolean;
  submittedAt: string;
  feedbackCycle?: {
    id: string;
    name: string;
  };
}

interface DeleteFeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  feedback: Feedback | null;
  onSuccess?: () => void;
}

export function DeleteFeedbackModal({
  isOpen,
  onClose,
  feedback,
  onSuccess
}: DeleteFeedbackModalProps) {
  const deleteFeedbackMutation = useDeleteFeedback();

  const handleDelete = async () => {
    if (!feedback) return;

    deleteFeedbackMutation.mutate(
      feedback.id,
      {
        onSuccess: () => {
          toast.success('Feedback deleted successfully!');
          onSuccess?.();
          onClose();
        },
        onError: (error: any) => {
          console.error("Error deleting feedback:", error);
          toast.error(`Failed to delete feedback: ${error?.message || 'Please try again.'}`);
        }
      }
    );
  };

  if (!feedback) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-card text-card-foreground border border-border shadow-lg">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex items-center justify-center">
              <Trash2 className="h-5 w-5 text-white" />
            </div>
            <DialogTitle className="text-xl font-bold text-foreground">
              Delete Feedback
            </DialogTitle>
          </div>
        </DialogHeader>

        <div className="space-y-6">
          {/* Warning Banner */}
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-medium text-red-800 mb-1">
                  This action cannot be undone
                </h3>
                <p className="text-sm text-red-700">
                  Deleting this feedback will permanently remove it from the system. This action is irreversible.
                </p>
              </div>
            </div>
          </div>

          {/* Feedback Info */}
          <div className="p-4 bg-muted/30 rounded-lg border border-border">
            <div className="space-y-2">
              {feedback.feedbackCycle?.name && (
                <p className="text-sm">
                  <span className="text-muted-foreground">Cycle:</span>{' '}
                  <span className="font-medium text-foreground">{feedback.feedbackCycle.name}</span>
                </p>
              )}
              <p className="text-sm">
                <span className="text-muted-foreground">Submitted:</span>{' '}
                <span className="font-medium text-foreground">{new Date(feedback.submittedAt).toLocaleDateString()}</span>
              </p>
              <p className="text-sm">
                <span className="text-muted-foreground">Anonymous:</span>{' '}
                <span className="font-medium text-foreground">{feedback.isAnonymous ? 'Yes' : 'No'}</span>
              </p>
              {feedback.responseRating && (
                <p className="text-sm">
                  <span className="text-muted-foreground">Rating:</span>{' '}
                  <span className="font-medium text-foreground">{feedback.responseRating} star{feedback.responseRating !== 1 ? 's' : ''}</span>
                </p>
              )}
            </div>

            <div className="mt-3 p-3 bg-background rounded border">
              <p className="text-sm text-muted-foreground font-medium mb-1">Feedback Content:</p>
              <p className="text-sm text-foreground line-clamp-3">
                {feedback.responseText}
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={deleteFeedbackMutation.isPending}
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              disabled={deleteFeedbackMutation.isPending}
              variant="destructive"
            >
              {deleteFeedbackMutation.isPending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Feedback
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
